/**
 * Rain as streaks (05 §9): short slanted one-texel lines, and a sparse tick where a drop has
 * just landed. Every drop belongs to a CELL OF THE WORLD, not to the camera: where it is at
 * time t is a pure function of (cell, slot, t), and the camera only decides which cells are
 * worth drawing. So scrolling never re-rolls the rain, and with the camera snapped to whole
 * texels a streak rasterises identically wherever the view is (the M0 stability property).
 */

import { BufferAttribute, BufferGeometry, type Color, LineBasicMaterial, LineSegments } from 'three';
import { patchMrtUnlit } from '../materials/toon';

export const RAIN = {
  /** Metres along a side of a world cell, and drops that live in each. */
  cell: 2,
  perCell: 2,
  /**
   * ROUND5: a thunderstorm's cell. It is heavy rain, not a new substance - the stroke and the clap
   * carry its character - so it is the same streaks in the same cells, twice as many of them.
   */
  heavyPerCell: 4,
  /** m/s downward, and the wind that slants it (x, z). */
  fall: 15,
  wind: [2.0, 0.5] as const,
  /** Drops start this high; one cycle is `top / fall` seconds. */
  top: 14,
  /** Length of a streak, as seconds of the drop's travel: about seven texels. */
  exposure: 0.045,
  /** A landed drop leaves a tick for this fraction of the next cycle; only slot 0 of a cell splashes. */
  splash: 0.09,
  splashHalfWidth: 0.07,
} as const;

/** Cells drawn: a window round the focus, reaching further SOUTH because height draws north on screen. */
const WINDOW = { west: 24, east: 24, north: 16, south: 28 } as const;
const NX = (WINDOW.west + WINDOW.east) / RAIN.cell;
const NZ = (WINDOW.north + WINDOW.south) / RAIN.cell;
/** Segments per drop: its streak, and the tick of the drop before it. */
const SEGMENTS = 2;
/** Where a segment with nothing to show is parked: beyond the far plane. */
const NOWHERE = -1000;

/** 0..1 from three integers: the only randomness rain has, and it has no state. */
export function cellHash(x: number, z: number, salt: number): number {
  let h = (x * 374761393 + z * 668265263 + salt * 1274126177) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Where cycle `cycle` of a drop comes down within its cell, on the ground plane. */
function landing(cx: number, cz: number, slot: number, cycle: number, out: { x: number; z: number }): void {
  out.x = (cx + cellHash(cx, cz, slot * 8 + 1 + cycle * 64)) * RAIN.cell;
  out.z = (cz + cellHash(cx, cz, slot * 8 + 2 + cycle * 64)) * RAIN.cell;
}

const _land = { x: 0, z: 0 };

/**
 * Write the two segments of drop `slot` of world cell (cx, cz) at `time` into `out` at `offset`
 * (12 floats: streak head, streak tail, tick left, tick right). `ground` is the height the
 * drop lands at. Pure: the same arguments always give the same numbers.
 */
export function writeDrop(
  out: Float32Array, offset: number, cx: number, cz: number, slot: number, time: number, ground: number,
): void {
  const period = RAIN.top / RAIN.fall;
  const turn = time / period + cellHash(cx, cz, slot * 8 + 3);
  const cycle = Math.floor(turn);
  const phase = turn - cycle;
  const y = RAIN.top * (1 - phase);
  const [wx, wz] = RAIN.wind;
  landing(cx, cz, slot, cycle, _land);
  // Falling with the wind: `y / fall` seconds from landing it is still that much wind short of it.
  const lead = y / RAIN.fall;
  out[offset] = _land.x - wx * lead;
  out[offset + 1] = ground + y;
  out[offset + 2] = _land.z - wz * lead;
  out[offset + 3] = _land.x - wx * (lead + RAIN.exposure);
  out[offset + 4] = ground + y + RAIN.fall * RAIN.exposure;
  out[offset + 5] = _land.z - wz * (lead + RAIN.exposure);

  const splashing = slot === 0 && phase < RAIN.splash;
  if (splashing) landing(cx, cz, slot, cycle - 1, _land);
  const sy = splashing ? ground + 0.04 : NOWHERE;
  out[offset + 6] = _land.x - RAIN.splashHalfWidth;
  out[offset + 7] = sy;
  out[offset + 8] = _land.z;
  out[offset + 9] = _land.x + RAIN.splashHalfWidth;
  out[offset + 10] = sy;
  out[offset + 11] = _land.z;
}

/** World cell whose drops fill window slot (i, j) when the view is centred on (fx, fz). */
export function windowCell(i: number, j: number, fx: number, fz: number): { cx: number; cz: number } {
  return {
    cx: Math.floor((fx - WINDOW.west) / RAIN.cell) + i,
    cz: Math.floor((fz - WINDOW.north) / RAIN.cell) + j,
  };
}

export class RainStreaks {
  readonly lines: LineSegments;
  private readonly positions: Float32Array;
  /** Ground height per world cell, looked up once: the ground does not move, and this runs every frame. */
  private readonly heights = new Map<number, number>();

  constructor(
    color: Color,
    private readonly groundY: ((x: number, z: number) => number) | null,
    /** Drops in each world cell: `RAIN.heavyPerCell` for a thunderstorm. Slots are salted apart. */
    private readonly perCell: number = RAIN.perCell,
  ) {
    this.positions = new Float32Array(NX * NZ * perCell * SEGMENTS * 2 * 3);
    const geometry = new BufferGeometry();
    // A plain BufferAttribute keeps OUR array (Float32BufferAttribute would copy it), so `update` writes in place.
    geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    const material = new LineBasicMaterial({ color });
    material.onBeforeCompile = patchMrtUnlit;
    material.customProgramCacheKey = () => 'lines-mrt';
    this.lines = new LineSegments(geometry, material);
    this.lines.frustumCulled = false;
    this.lines.name = 'rain';
  }

  /** Floats per drop in `buffer`, and how the window is laid out: for tests. */
  static readonly layout = { nx: NX, nz: NZ, floatsPerDrop: SEGMENTS * 2 * 3 } as const;

  /** Drops per cell this field was built with: how many slots `buffer` holds for each one. */
  get slots(): number {
    return this.perCell;
  }

  get buffer(): Float32Array {
    return this.positions;
  }

  /** One height per cell is enough: a drop is a texel wide and gone in a frame or two. */
  private groundAt(cx: number, cz: number): number {
    if (!this.groundY) return 0;
    const key = (cx + 32768) * 65536 + (cz + 32768);
    let y = this.heights.get(key);
    if (y === undefined) {
      y = this.groundY((cx + 0.5) * RAIN.cell, (cz + 0.5) * RAIN.cell);
      this.heights.set(key, y);
    }
    return y;
  }

  update(time: number, fx: number, fz: number): void {
    let offset = 0;
    const { cx: x0, cz: z0 } = windowCell(0, 0, fx, fz);
    for (let j = 0; j < NZ; j++) {
      for (let i = 0; i < NX; i++) {
        const cx = x0 + i;
        const cz = z0 + j;
        const ground = this.groundAt(cx, cz);
        for (let slot = 0; slot < this.perCell; slot++) {
          writeDrop(this.positions, offset, cx, cz, slot, time, ground);
          offset += SEGMENTS * 2 * 3;
        }
      }
    }
    this.lines.geometry.getAttribute('position').needsUpdate = true;
  }
}
